// 새 형식 장면(v2)의 준비·사주 변형·돈 효과. 데이터는 ../data/scenes.js, 설계는 docs/event-system-v2-design.md.
// 사주는 세 군데에 쓰인다: 장면이 나오는 시기, 선택지 보상·위험(natal 규칙), 결과 설명(natalReason).
import { opensFor } from './engine.js';
import { natalProfile } from './life.js';
import { luckAt } from './luckPhase.js';
import { classifyTransition } from './luckTransition.js';
import { essenceFor } from './events.js';
import { personalIncome, occupationOf, OCCUPATION_LABEL, WORK_START_AGE, incomeIndex, fillMoney } from './economy.js';
import { getLifeTables } from './lifeEvents.js';

export const SCENE_MODEL = 'scene-v1';
const G = { 관계: '비겁', 활동: '식상', 재물: '재성', 직업: '관성', 학업: '인성' }; // natal 조건의 영역 이름 → 엔진 영역 키
const BRANCH_MAIN_YEAR = (year) => (((year - 4) % 12) + 12) % 12;
const HAP = [[0, 1], [2, 11], [3, 10], [4, 9], [5, 8], [6, 7]];
const isHap = (a, b) => HAP.some(([x, y]) => (a === x && b === y) || (a === y && b === x));

const incomeOf = (s, age) => Math.round(s.life?.economy?.income || personalIncome(s, age));
const cashOf = (s) => Math.max(0, Math.round(s.life?.economy?.cash ?? 0));
const stakeOf = (s, age) => { const f = incomeIndex(s.birthYear + age); return Math.min(5000 * f, Math.max(500 * f, Math.round(incomeOf(s, age) * 0.5))); };

/** 문구의 {…} 에 넣을 값. 모두 경제 상태에서 계산한다. */
export function sceneVars(s, age = s.age) {
  const income = incomeOf(s, age), t = getLifeTables().economy;
  return {
    직종: OCCUPATION_LABEL[occupationOf(s.job)] ?? '', firstIncome: Math.round(personalIncome(s, age)).toLocaleString(),
    income: income.toLocaleString(), offer: Math.round(income * 1.1).toLocaleString(), stake: stakeOf(s, age).toLocaleString(),
    cash: cashOf(s).toLocaleString(), bonus: Math.round(income * 1.5).toLocaleString(), eduCost: Math.round(292 * incomeIndex(s.birthYear + age)).toLocaleString(), early: Math.round(t.seniorIncome * 0.7 * incomeIndex(s.birthYear + age)).toLocaleString(),
  };
}
export const fillScene = (text, vars) => String(text ?? '').replace(/\{([^}]+)\}/g, (all, key) => vars[key] ?? all);

// working: 직업이 있고 임금근로 상태가 '쉬는 중'이 아닐 때(소득이 있는 해)만 소득 관련 장면이 나온다.
const REQUIRE = {
  male: (s) => s.birth?.gender !== 'F', married: (s) => s.life?.marriage?.status === 'married', college: (s) => s.life?.education === 'college',
  kidUnder6: (s) => (s.life?.children ?? []).some((c) => s.age - c.bornAge <= 5),
  kidSchool: (s) => (s.life?.children ?? []).some((c) => s.age - c.bornAge >= 8 && s.age - c.bornAge <= 18),
  job: (s) => !!s.job, cash: (s) => cashOf(s) >= 500, working: (s) => !!s.job && s.life?.work?.state !== 'O' };

function matches(s, when, ctx) {
  if (when.open) return ctx.opens.includes(G[when.open] ?? when.open);
  if (when.strong) return natalProfile(s, G[when.strong] ?? when.strong).kind === 'high';
  if (when.weak) return natalProfile(s, G[when.weak] ?? when.weak).kind === 'entry';
  if (when.transition) return ctx.transition === when.transition;
  if (when.yong) return essenceFor(s.saju)?.yong?.value === when.yong;
  if (when.palaceHap) {
    const palace = s.saju?.day?.branch;
    const branches = [BRANCH_MAIN_YEAR(s.birthYear + s.age), ctx.luck.daewoon?.branch].filter(Number.isInteger);
    return Number.isInteger(palace) && branches.some((b) => isHap(palace, b));
  }
  return false;
}

const round1 = (v) => Math.round(v * 10) / 10;
const scaleEffects = (effects, positive, negative) => Object.fromEntries(Object.entries(effects ?? {})
  .map(([k, v]) => [k, v > 0 ? round1(v * positive) : v < 0 ? round1(v * negative) : v]));

/** 장면을 지금 나이·원국에 맞게 만든다. 조건이 안 맞으면 null. */
export function prepareScene(s, event) {
  if ((event.requires ?? []).some((key) => !REQUIRE[key]?.(s))) return null;
  const luck = luckAt(s.saju, s.age);
  const ctx = { opens: opensFor(s, s.age).open, luck, transition: luck.inTransition ? classifyTransition(essenceFor(s.saju), luck) : null };
  const vars = sceneVars(s), notes = [];
  const choices = event.choices.map((c) => ({ ...c, effects: { ...c.effects }, failureEffects: { ...c.failureEffects } }));
  for (const rule of event.natal ?? []) {
    if (!matches(s, rule.when, ctx)) continue;
    const c = choices.find((x) => x.slot === rule.slot);
    if (!c) continue;
    if (rule.rewardScale || rule.lossScale) {
      c.effects = scaleEffects(c.effects, rule.rewardScale ?? 1, 1);
      c.failureEffects = scaleEffects(c.failureEffects, 1, rule.lossScale ?? 1);
      if (c.targetField && c.effects[c.targetField] != null) c.delta = c.effects[c.targetField];
    }
    if (rule.riskDelta) c.risk = Math.min(0.9, Math.max(0, Math.round(((c.risk ?? 0) + rule.riskDelta) * 100) / 100));
    notes.push(rule.note);
  }
  // 군 복무: 대학에 다니는 중이면 입대는 휴학이다(학업 -1). 복학과 졸업이 늦어지는 만큼 첫 직업도 늦어진다.
  let extra = '';
  if (event.id === 's_military' && s.life?.education === 'college') {
    extra = ' 대학에 다니는 중이라 입대하면 휴학하게 된다.';
    for (const c of choices) if (c.slot === 'stable') { c.effects = { ...c.effects, 인성: -1 }; c.hint = '직업↑ 관계↑ · 휴학(학업↓) · 첫 직장이 2년 늦어짐'; }
  }
  return priceChoices({ ...event, title: fillScene(event.title, vars), description: fillScene(event.description, vars) + extra, choices,
    natalReason: notes.join(' ') || null }, s);
}

/** 선택지 힌트와 설명의 {만원:숫자} 를 그해 가격으로 바꾼다. */
export const priceChoices = (event, s) => ({ ...event, description: fillMoney(event.description, s, s.age), choices: event.choices.map((c) => ({ ...c, hint: fillMoney(c.hint, s, s.age) })) });

// ───────── 돈 효과 ─────────
const BASE = { income: incomeOf, cash: (s) => cashOf(s), stake: stakeOf, bonus: (s, age) => Math.round(incomeOf(s, age) * 1.5) };

/** 선택의 돈 효과를 경제 상태에 반영한다(결과에 따라 success/fail, 결과와 무관하게 always). 24세 전이면 건너뛴다. */
export function applySceneMoney(s, event, choice, failed, age) {
  const money = choice.money, e = s.life?.economy;
  if (!money || !e || age < WORK_START_AGE) return;
  const parts = [];
  for (const part of [money.always, failed ? money.fail : money.success]) {
    if (!part) continue;
    if (part.net) { const amount = part.net.abs != null ? Math.round(part.net.abs * incomeIndex(s.birthYear + age)) : Math.round(BASE[part.net.of](s, age) * part.net.pct); e.cash += amount; parts.push(`순자산 ${amount > 0 ? '+' : ''}${amount.toLocaleString()}만원`); }
    if (part.incomeMod) { (e.incomeMods ??= []).push({ pct: part.incomeMod.pct, until: age + part.incomeMod.years - 1 }); parts.push(`연 소득 ${part.incomeMod.pct > 0 ? '+' : ''}${Math.round(part.incomeMod.pct * 100)}%${part.incomeMod.years < 99 ? ` (${part.incomeMod.years}년)` : ''}`); }
    if (part.medical) { // 병원비: 이번 해 치료비를 냈거나(paid) 미뤘다(deferred, 내년에 1.5배로 낸다). 정산에서 같은 해 병원비를 또 내지 않게 표시한다.
      e.medicalPaid = age;
      if (part.medical === 'deferred') e.medicalDeferred = { from: age + 1, amount: Math.round(750 * incomeIndex(s.birthYear + age)) };
    }
    if (part.retire) e.retired = true;
    if (part.workUntil != null) e.workUntil = part.workUntil;
    if (part.pension) e.pension = { start: part.pension.start === 'now' ? age : part.pension.start, mult: part.pension.mult };
  }
  e.sceneLog = [...(e.sceneLog ?? []), { age, eventId: event.id, label: choice.label, failed: !!failed, money: parts.join(' · ') }];
}

/** 진로·입시 장면의 선택으로 학력이 정해지고(edu), 군 복무 같은 선택으로 첫 직업 시작이 늦어진다(delay, 년). */
export function applySceneLife(s, choice, failed) {
  if (!s.life) return;
  const pick = (v) => (v == null ? null : typeof v !== 'object' ? v : failed ? v.fail : v.success);
  const edu = pick(choice.edu), delay = pick(choice.delay);
  if (edu) s.life.education = edu;
  if (delay) s.life.jobDelay = (s.life.jobDelay ?? 0) + delay;
}
